import { TextField } from '@mui/material';
import { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Controller, useFormContext } from 'react-hook-form';

type Props = {
  name: string;
  label: string;
  contrast?: number;
  /** When true, an empty value is allowed (no hex required). */
  optional?: boolean;
};

const VNColorPicker = ({ name, label, contrast, optional = false }: Props) => {
  const { control } = useFormContext();
  const [showPicker, setShowPicker] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value) => {
          const raw = Array.isArray(value) ? value[0] : value;
          if (optional && (raw === undefined || raw === null || raw === '')) {
            return true;
          }
          const hexRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
          return hexRegex.test(raw) || 'Invalid hex color';
        },
      }}
      render={({ field: { onChange, value }, fieldState }) => {
        const isArray = Array.isArray(value);
        const colorValue = isArray ? value[0] || '' : value || '';

        return (
          <div
            className="vn-color-picker"
            onFocus={() => setShowPicker(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setShowPicker(false);
              }
            }}
          >
            <div className="input-container">
              <TextField
                label={label}
                value={colorValue}
                size="small"
                onChange={(e) => {
                  const newColor = e.target.value.replace(/[^#0-9A-Fa-f]/g, ''); // Remove special characters
                  onChange(isArray ? [newColor] : newColor); // ✅ Keeps array structure
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
              <button className="color-picker-button" style={{ backgroundColor: colorValue }} type="button">
                {colorValue}
              </button>
              {showPicker && (
                <div className="color-picker-container">
                  <div className="color-picker">
                    <HexColorPicker
                      color={colorValue}
                      onChange={(newColor) => onChange(isArray ? [newColor] : newColor)} // ✅ Keeps array structure
                    />
                  </div>
                </div>
              )}
            </div>
            {Boolean(contrast) && (
              <div className="vn-color-picker-contrast">
                {contrast >= 4.5 ? (
                  <span>Contrast ratio - {contrast.toFixed(2)}:1</span>
                ) : (
                  <span className="text-danger">
                    Selected accent color does not comply with{' '}
                    <a
                      href="https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html"
                      target="_blank"
                      rel="noreferrer"
                      className="text-danger"
                    >
                      WCAG&apos;s Level AA
                    </a>{' '}
                    minimum contrast ratio - {contrast.toFixed(2)}:1 (minimum - 4.5:1)
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default VNColorPicker;
